import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import type {
  IImmunizationDose,
  IImmunizationRecord,
} from 'src/interfaces/family'
import { exemptionReasonOptions } from 'src/config/options'

dayjs.extend(isSameOrBefore)

export type ImmunizationRecordStatus =
  | 'Complete'
  | 'Overdue'
  | 'Exempt'
  | 'Alternative'
  | 'Upcoming'
  | 'Missing Info'

interface ImmunizationRecordStatusMeta {
  label: ImmunizationRecordStatus
  color: string
  textColor: string
  icon: string
}

const statusMeta: Record<
  ImmunizationRecordStatus,
  ImmunizationRecordStatusMeta
> = {
  Complete: {
    label: 'Complete',
    color: 'positive',
    textColor: 'white',
    icon: 'check_circle',
  },
  Overdue: {
    label: 'Overdue',
    color: 'negative',
    textColor: 'white',
    icon: 'error',
  },
  Exempt: {
    label: 'Exempt',
    color: 'orange',
    textColor: 'white',
    icon: 'privacy_tip',
  },
  Alternative: {
    label: 'Alternative',
    color: 'orange',
    textColor: 'white',
    icon: 'event_note',
  },
  Upcoming: {
    label: 'Upcoming',
    color: 'blue-grey-5',
    textColor: 'white',
    icon: 'event_upcoming',
  },
  'Missing Info': {
    label: 'Missing Info',
    color: 'grey-5',
    textColor: 'white',
    icon: 'help_outline',
  },
}

export function shouldPromptForDoseCompletion(dose: IImmunizationDose) {
  const dueDate = dayjs(dose.dueDate)
  return (
    dueDate.isValid() &&
    dueDate.isSameOrBefore(dayjs(), 'day') &&
    (!dose.dateGiven || !dose.fileUrls?.length)
  )
}

export function shouldPromptForExemptionFile(
  record: IImmunizationRecord,
) {
  return Boolean(
    record.exemptionReason && !record.exemptionFileUrls?.length,
  )
}

export function shouldPromptForDoseLessRecordCompletion(
  record: IImmunizationRecord,
) {
  return Boolean(
    !record.doses?.length &&
      (!record.lastDoseDate || !record.recordFileUrls?.length),
  )
}

function deriveImmunizationRecordStatus(
  record: IImmunizationRecord,
): ImmunizationRecordStatus {
  if (record.exemptionReason === 'Alternative Dr. Approved Schedule') {
    return record.exemptionFileUrls?.length ? 'Alternative' : 'Missing Info'
  }

  if (
    record.exemptionReason &&
    exemptionReasonOptions
      .filter((reason) => reason !== 'Alternative Dr. Approved Schedule')
      .includes(record.exemptionReason)
  ) {
    return record.exemptionFileUrls?.length ? 'Exempt' : 'Missing Info'
  }

  if (record.doses?.length) {
    const hasOverdueIncompleteDoseInfo = record.doses.some((dose) => {
      if (!shouldPromptForDoseCompletion(dose)) return false

      return Boolean(dose.dateGiven) !== Boolean(dose.fileUrls?.length)
    })
    if (hasOverdueIncompleteDoseInfo) return 'Missing Info'

    const nextDose = record.doses.find(
      (dose) => !dose.dateGiven || !dose.fileUrls?.length,
    )
    if (!nextDose) return 'Complete'

    const parsedDueDate = dayjs(nextDose.dueDate)
    if (
      parsedDueDate.isValid() &&
      parsedDueDate.isSameOrBefore(dayjs(), 'day')
    ) {
      return 'Overdue'
    }

    return 'Upcoming'
  }

  const nextDueDate = record.nextDueDate
  const hasLastDoseDate = Boolean(record.lastDoseDate)
  const hasRecordFile = Boolean(record.recordFileUrls?.length)

  if (hasLastDoseDate !== hasRecordFile) return 'Missing Info'

  if (nextDueDate && nextDueDate !== 'N/A') {
    const parsedNextDueDate = dayjs(nextDueDate)
    if (
      parsedNextDueDate.isValid() &&
      parsedNextDueDate.isSameOrBefore(dayjs(), 'day')
    ) {
      return 'Overdue'
    }
    if (parsedNextDueDate.isValid()) return 'Upcoming'
  }

  if (hasLastDoseDate && hasRecordFile) return 'Complete'

  return 'Missing Info'
}

export function getImmunizationRecordStatus(
  record: IImmunizationRecord,
): ImmunizationRecordStatus {
  return deriveImmunizationRecordStatus(record)
}

export function getImmunizationRecordStatusMeta(
  record: IImmunizationRecord,
): ImmunizationRecordStatusMeta {
  return statusMeta[getImmunizationRecordStatus(record)]
}

export function shouldShowImmunizationExpiryAlert(record: IImmunizationRecord) {
  const status = getImmunizationRecordStatus(record)
  return (
    (status === 'Complete' || status === 'Upcoming' || status === 'Overdue') &&
    !!record.nextDueDate &&
    record.nextDueDate !== 'N/A'
  )
}
